import { useSelector } from "react-redux";

/**
 * Get user permissions from Redux state
 */
export const usePermissions = () => {
  const { user } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);

  // If user is admin, return all permissions
  if (user?.role === "admin") {
    return {
      hasPermission: () => true,
      hasAnyPermission: () => true,
      hasAllPermissions: () => true,
      canAccess: () => true,
    };
  }

  // Find user's role from active roles
  const userRole = activeRoles.find((role) => role.name === user?.role);

  // Get permissions for user's role
  const permissions = userRole?.permissions || [];

  /**
   * Check if user has specific permission
   */
  const hasPermission = (resource, action) => {
    if (user?.role === "admin") return true;
    if (!userRole) return false;

    const permission = permissions.find((p) => p.resource === resource);
    return permission && permission.actions.includes(action);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionList) => {
    if (user?.role === "admin") return true;
    if (!userRole) return false;

    return permissionList.some(({ resource, action }) =>
      hasPermission(resource, action)
    );
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionList) => {
    if (user?.role === "admin") return true;
    if (!userRole) return false;

    return permissionList.every(({ resource, action }) =>
      hasPermission(resource, action)
    );
  };

  /**
   * Check if user can access a resource (has any action)
   */
  const canAccess = (resource) => {
    if (user?.role === "admin") return true;
    if (!userRole) return false;

    const permission = permissions.find((p) => p.resource === resource);
    return permission && permission.actions.length > 0;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    permissions,
    userRole,
  };
};

/**
 * Hook to check if user has permission (for use in components)
 */
export const useHasPermission = (resource, action) => {
  const { hasPermission } = usePermissions();
  return hasPermission(resource, action);
};

/**
 * Hook to check if user can access resource
 */
export const useCanAccess = (resource) => {
  const { canAccess } = usePermissions();
  return canAccess(resource);
};

